import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useCreators } from "@/context/CreatorsContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check } from "lucide-react";
import InAppBrowserRedirect from "@/components/InAppBrowserRedirect";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a4b1710a-e8aa-40f2-90ed-6aa15c96d321/artifacts/w14lowsf_Disen%CC%83o%20sin%20ti%CC%81tulo.png";

// Phone Card Component
const PhoneCard = ({ creator, size = "normal" }) => {
  if (!creator) return null;
  
  const isSmall = size === "small";
  
  return (
    <Link to={`/${creator.username}`} className="group block">
      <div className={`bg-black rounded-[2rem] overflow-hidden shadow-2xl border border-white/10 hover:border-white/30 transition-all ${
        isSmall ? 'w-[200px]' : 'w-[260px]'
      }`}>
        {/* Phone notch */}
        <div className="h-5 bg-black flex items-center justify-center">
          <div className="w-16 h-4 bg-black rounded-full border-b border-white/10" />
        </div>
        
        {/* Profile Image */}
        <div className={`relative overflow-hidden ${isSmall ? 'aspect-[3/4]' : 'aspect-[3/4]'}`}>
          <img
            src={creator.coverImage || creator.avatar}
            alt={creator.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          
          {/* Profile Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <h3 className={`font-bold text-white mb-0.5 ${isSmall ? 'text-sm' : 'text-lg'}`}>{creator.name}</h3>
            <p className={`text-white/60 ${isSmall ? 'text-xs' : 'text-sm'}`}>@{creator.username}</p>
            
            {/* Social Icons */}
            {creator.socialLinks && !isSmall && (
              <div className="flex items-center gap-2 mt-2">
                {creator.socialLinks.onlyfans && (
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-[10px]">OF</span>
                  </div>
                )}
                {creator.socialLinks.instagram && (
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-[10px]">IG</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Featured Links - Only for normal size */}
        {!isSmall && creator.links && creator.links.length > 0 && (
          <div className="p-2 space-y-1.5">
            {creator.links.slice(0, 2).map((link) => (
              <div 
                key={link.id}
                className="flex items-center gap-2 p-1.5 rounded-lg bg-white/5 text-xs"
              >
                {link.image ? (
                  <img src={link.image} alt="" className="w-6 h-6 rounded object-cover" />
                ) : (
                  <div className="w-6 h-6 rounded bg-white/10" />
                )}
                <span className="text-white font-medium truncate flex-1">{link.title}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* Bottom bar */}
        <div className="h-3 flex items-center justify-center pb-1">
          <div className="w-16 h-1 bg-white/20 rounded-full" />
        </div>
      </div>
    </Link>
  );
};

const HomePage = () => {
  const { creators, getFeaturedCreators } = useCreators();
  const featuredCreators = getFeaturedCreators();
  const featuredCreator = featuredCreators[0];
  
  // Get all active creators for carousel
  const allCreators = creators.filter(c => c.active);
  
  // Carousel state for bottom section
  const [carouselIndex, setCarouselIndex] = useState(0);
  
  // Auto-rotate carousel
  useEffect(() => {
    if (allCreators.length <= 1) return;
    
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % allCreators.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [allCreators.length]);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <InAppBrowserRedirect />
      
      <Helmet>
        <title>JGlinks - Multilink para Modelos | Link in Bio Premium</title>
        <meta name="description" content="JGlinks es la plataforma multilink premium para modelos y creadoras de contenido. Crea tu perfil con links, galería, redes sociales y contenido exclusivo VIP." />
      </Helmet>

      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover opacity-60"
          onCanPlay={(e) => e.target.play()}
        >
          <source src="/background.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
      </div>

      {/* Navigation */}
      <header className="relative z-50 px-4 md:px-8 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="JGlinks" className="h-8 md:h-10 w-auto" />
          </Link>

          <div className="flex items-center gap-2">
            <Link to="/explorar" className="hidden sm:block">
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">
                Explorar
              </Button>
            </Link>
            <Link to="/modelo/registro">
              <Button size="sm" className="rounded-full bg-white text-black hover:bg-white/90 px-4 text-sm">
                Únete
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Content */}
      <main className="relative z-10 px-4 md:px-8 pt-4 md:pt-8 pb-16">
        <div className="max-w-6xl mx-auto">
          
          {/* Hero Section - Text + Phone Card Side by Side */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12 mb-16 md:mb-24">
            
            {/* Text Content */}
            <div className="flex-1 text-center lg:text-left mb-8 lg:mb-0">
              
              {/* Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4">
                <span className="text-white">Un multilink que</span>
                <br />
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  simplifica y aumenta
                </span>
                <br />
                <span className="text-white">tus ventas</span>
              </h1>

              {/* Subtitle */}
              <p className="text-white/60 text-sm md:text-base mb-6 max-w-md mx-auto lg:mx-0">
                Mejores resultados con mayor tasa de conversión.
              </p>

              {/* Benefits */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center lg:justify-start mb-6">
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span>Sin publicidad</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span>Evita baneos</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span>100% seguro</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6">
                <Link to="/modelo/registro">
                  <Button className="w-full sm:w-auto rounded-full bg-white text-black hover:bg-white/90 px-8 h-12">
                    Regístrate ahora
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/explorar">
                  <Button variant="outline" className="w-full sm:w-auto rounded-full border-white/20 text-white hover:bg-white/10 px-6 h-12">
                    Explorar
                  </Button>
                </Link>
              </div>

              {/* Pricing Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <span className="text-green-400 text-sm font-medium">1er mes gratis</span>
                <span className="text-white/40 text-sm">·</span>
                <span className="text-white/70 text-sm">luego $2.99/mes</span>
              </div>

              {/* Ambassador CTA */}
              <Link
                to="/embajadoras"
                data-testid="ambassador-cta-home"
                className="group mt-5 inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-gradient-to-r from-rose-500/15 to-amber-500/15 border border-rose-400/30 hover:from-rose-500/25 hover:to-amber-500/25 hover:border-rose-300/50 transition-all"
              >
                <span className="text-xs uppercase tracking-wider font-bold bg-gradient-to-r from-rose-300 to-amber-200 bg-clip-text text-transparent">
                  ✨ ¿Quieres ser parte de nuestro equipo de embajadoras?
                </span>
                <ArrowRight className="w-4 h-4 text-rose-300 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Phone Card - Hero */}
            {featuredCreator && (
              <div className="flex justify-center lg:justify-end flex-shrink-0">
                <div className="relative">
                  <div className="absolute -inset-8 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 blur-3xl rounded-full opacity-50" />
                  <div className="relative">
                    <PhoneCard creator={featuredCreator} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Comparison Section */}
          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4">
              ¿Por qué JGlinks?
            </h2>
            <p className="text-white/50 text-center text-sm md:text-base mb-10 max-w-xl mx-auto">
              Otros multilinks arruinan tu imagen y alejan a tus fans
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
              {/* Others - Bad */}
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="font-semibold text-white/50">Otros multilinks</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-white/50 text-sm">
                    <span className="text-white/30 mt-0.5">—</span>
                    <span>Publicidad de otras creadoras en tu perfil</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/50 text-sm">
                    <span className="text-white/30 mt-0.5">—</span>
                    <span>Diseños genéricos y poco profesionales</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/50 text-sm">
                    <span className="text-white/30 mt-0.5">—</span>
                    <span>Desvían la atención hacia otros perfiles</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/50 text-sm">
                    <span className="text-white/30 mt-0.5">—</span>
                    <span>Riesgo de baneo en Instagram</span>
                  </li>
                </ul>
              </div>
              
              {/* JGlinks - Good */}
              <div className="bg-white/5 border border-white/20 rounded-2xl p-5 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="font-semibold text-white">JGlinks</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-white/80 text-sm">
                    <span className="text-cyan-400 mt-0.5">✓</span>
                    <span>Cero publicidad - Tu perfil, solo tú</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/80 text-sm">
                    <span className="text-cyan-400 mt-0.5">✓</span>
                    <span>Diseño premium y elegante</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/80 text-sm">
                    <span className="text-cyan-400 mt-0.5">✓</span>
                    <span>100% de atención en tu contenido</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/80 text-sm">
                    <span className="text-cyan-400 mt-0.5">✓</span>
                    <span>Redirección segura que evita baneos</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Creators Carousel Section */}
          {allCreators.length > 0 && (
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4">
                Creadoras que ya confían en nosotros
              </h2>
              <p className="text-white/50 text-center text-sm md:text-base mb-10 max-w-xl mx-auto">
                Únete a nuestra comunidad de creadoras exitosas
              </p>
              
              {/* Carousel */}
              <div className="relative overflow-hidden mb-8">
                <div 
                  className="flex justify-center items-end gap-4 transition-transform duration-500"
                  style={{
                    transform: `translateX(${-carouselIndex * 0}px)`
                  }}
                >
                  {allCreators.map((creator, index) => {
                    const isActive = index === carouselIndex;
                    const isPrev = index === (carouselIndex - 1 + allCreators.length) % allCreators.length;
                    const isNext = index === (carouselIndex + 1) % allCreators.length;
                    const isVisible = isActive || isPrev || isNext || allCreators.length <= 3;
                    
                    if (!isVisible && allCreators.length > 3) return null;
                    
                    return (
                      <div
                        key={creator.username || index}
                        className={`transition-all duration-500 flex-shrink-0 ${
                          isActive 
                            ? 'scale-100 opacity-100 z-20' 
                            : 'scale-90 opacity-50 z-10'
                        } ${!isActive && allCreators.length > 1 ? 'hidden md:block' : ''}`}
                      >
                        <PhoneCard creator={creator} size={isActive ? "normal" : "small"} />
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Navigation dots */}
              {allCreators.length > 1 && (
                <div className="flex items-center justify-center gap-2 mb-8">
                  {allCreators.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCarouselIndex(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === carouselIndex ? 'bg-white w-6' : 'bg-white/30 w-2'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* CTA */}
              <div className="text-center">
                <p className="text-white/50 text-sm mb-3">¿Eres creadora?</p>
                <Link to="/modelo/registro">
                  <Button className="rounded-full bg-white text-black hover:bg-white/90 px-8 h-11">
                    Crear mi perfil gratis
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default HomePage;
